import React from 'react';
import cn from 'classnames';
import ModalContactLinksPanel from './ModalContactLinksPanel';
import ModalInstructionsPanel from './ModalInstructionsPanel';

type Point = {
  x: number;
  y: number;
};

interface SamplePageModalProps {
  isOpen: boolean;
  onClose: () => void;
  page: React.ReactNode;
  title?: string;
  instructionalText?: string[];
  contactInfo?: Array<{ school: string; url: string }>;
}

const styles = {
  overlay: 'fixed inset-0 z-50 bg-black/40 mt-20',
  modal: 'absolute left-1/2 top-1/2 flex h-[min(70vh,900px)] w-[min(1100px,82vw)] flex-col overflow-hidden rounded-xl bg-white shadow-2xl',
  header: 'mt-1 flex shrink-0 cursor-move items-center justify-between border-b border-brand-border px-4 py-3',
  title: 'text-brand-navy text-lg font-bold leading-7',
  closeButton: 'rounded border border-brand-border px-3 py-1 text-sm font-semibold text-brand-navy hover:bg-slate-50 cursor-pointer hover:shadow-lg',
  body: 'flex flex-1 flex-col overflow-auto',
  pageWrap: 'min-h-0 flex-1 rounded-md border border-brand-border bg-white p-2 ml-1',
  footer: 'mt-3 flex shrink-0 justify-end px-4 pb-4',
};

const SampleDocumentModal = ({ isOpen, onClose, page, title = 'Sample Document', instructionalText, contactInfo }: SamplePageModalProps) => {
  const [offset, setOffset] = React.useState<Point>({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStartRef = React.useRef<{ point: Point; offset: Point } | null>(null);
  const modalRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      setOffset({ x: 0, y: 0 });
      setIsDragging(false);
      dragStartRef.current = null;
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
    return undefined;
  }, [isOpen]);

  React.useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (!dragStartRef.current) {
        return;
      }

      const { point, offset: startOffset } = dragStartRef.current;
      const nextOffset = {
        x: startOffset.x + event.clientX - point.x,
        y: startOffset.y + event.clientY - point.y,
      };
      const modalRect = modalRef.current?.getBoundingClientRect();

      if (!modalRect) {
        setOffset(nextOffset);
        return;
      }

      const maxX = Math.max(0, window.innerWidth / 2 - modalRect.width / 2);
      const maxY = Math.max(0, window.innerHeight / 2 - modalRect.height / 2);

      setOffset({
        x: Math.min(maxX, Math.max(-maxX, nextOffset.x)),
        y: Math.min(maxY, Math.max(-maxY, nextOffset.y)),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const handleDragStart = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      point: { x: event.clientX, y: event.clientY },
      offset,
    };
  };

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className={cn(styles.overlay)} role='dialog' aria-modal='true' onClick={onClose}>
      <div
        ref={modalRef}
        className={cn(styles.modal)}
        onClick={(event) => event.stopPropagation()}
        style={{
          transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
        }}
      >
        <div className={cn(styles.header)} onMouseDown={handleDragStart}>
          <div className={cn(styles.title)}>{title}</div>
          <button
            type='button'
            className={cn(styles.closeButton, 'hover:bg-gray-200 hover:text-red-500 transition-shadow')}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={onClose}
          >
            X
          </button>
        </div>
        <div className={cn(styles.body)} onWheel={handleWheel}>
          <ModalInstructionsPanel instructions={instructionalText ?? []} />
          <ModalContactLinksPanel contacts={contactInfo ?? []} />
          <div id='page-wrap' className={cn(styles.pageWrap)}>
            {page}
          </div>
        </div>
        <div className={cn(styles.footer)}>
          <button
            type='button'
            className={cn(
              styles.closeButton,
              'w-[130px] bg-blue rounded-3xl border-none text-white text-sm font-bold cursor-pointer hover:shadow-lg hover:bg-gray-200 hover:text-dark-blue transition-shadow'
            )}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SampleDocumentModal;
